import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { getPortfolioSafetyRoles } from 'services/portfolioService'
import { getEstateSafetyRoles } from 'services/estateService'
import SafetyRolesExplainer from 'features/compliance/SafetyRolesExplainer'
import SafetyRoleEditor from 'features/accessControl/safetyRoles/SafetyRoleEditor'
import useSafetyRoleCandidates from 'features/accessControl/safetyRoles/useSafetyRoleCandidates'
import useDutyholderOrganisations from 'features/accessControl/safetyRoles/useDutyholderOrganisations'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { SkeletonLine } from 'components/Skeleton'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { ink, line, radius, surface, font, bp as bpToken } from 'features/dashboard/tokens'

const PAGE = 25

const ROLE_PURPOSE = {
  responsible_person: 'Legally responsible for fire safety in the building.',
  principal_accountable_person: 'Overall responsibility for safety in a higher-risk building.',
  principal_designer: 'Makes sure the design work meets Building Regulations.'
}

const CELL_ACTION = { missing: 'Set', assigned: 'Change', na: 'Change' }

const RoleCoverageView = ({ scopeType, scopeId, onChanged }) => {
  const navigate = useNavigate()
  const isEstate = scopeType === 'estate'
  const isSupported = isEstate || scopeType === 'portfolio'
  const scopeLabel = isEstate ? 'estate' : 'portfolio'
  const scope = useMemo(() => ({ type: isEstate ? 'estate' : 'portfolio', id: scopeId }), [isEstate, scopeId])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(isSupported)
  const [search, setSearch] = useState('')
  const [visible, setVisible] = useState(PAGE)
  const [bulkRole, setBulkRole] = useState(null)
  const [cellEdit, setCellEdit] = useState(null)
  const candidates = useSafetyRoleCandidates(scope)
  const organisations = useDutyholderOrganisations()

  const load = useCallback(async () => {
    if (!isSupported) return
    try {
      const res = isEstate ? await getEstateSafetyRoles(scopeId) : await getPortfolioSafetyRoles(scopeId)
      setData(res?.data?.data || null)
    } catch (e) {
      toast.error(`Could not load the safety roles for this ${scopeLabel}.`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [scopeId, isSupported, isEstate, scopeLabel])

  useEffect(() => { load() }, [load])

  const buildings = data?.buildings || []
  const roleTotals = data?.roleTotals || []
  const totalsFor = key => {
    const t = roleTotals.find(x => x.role === key) || { assigned: 0, na: 0, missing: 0 }
    return { ...t, total: (t.assigned || 0) + (t.na || 0) + (t.missing || 0) }
  }

  const roleMeta = useMemo(() => {
    const first = buildings[0]
    return (first?.roles || []).map(r => ({ key: r.key, label: r.label }))
  }, [buildings])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return buildings
    return buildings.filter(b => (b.name || '').toLowerCase().includes(q))
  }, [buildings, search])

  useEffect(() => { setVisible(PAGE) }, [search])

  const shown = filtered.slice(0, visible)

  const missingIdsFor = roleKey => buildings
    .filter(b => b.canManage !== false && (b.roles || []).some(r => r.key === roleKey && r.state === 'missing'))
    .map(b => b.id)

  const afterChange = async () => {
    await load()
    onChanged?.()
  }

  const openCell = (bp, role) => {
    const same = cellEdit && cellEdit.bpId === bp.id && cellEdit.roleKey === role.key
    setCellEdit(same ? null : { bpId: bp.id, roleKey: role.key })
  }

  if (!isSupported) {
    return (
      <Wrap>
        <Explainer>
          <InfoOutlinedIcon style={{ fontSize: 18 }} />
          <ExplainerText>
            <ExplainerTitle>Role coverage isn't available here</ExplainerTitle>
            <ExplainerBody>
              Safety roles, such as responsible person, principal accountable person and principal
              designer, are recorded per building and summarised across a portfolio or estate. Open
              a portfolio or estate to see who holds each role and set them in bulk, or check an
              individual Building Passport's Access tab.
            </ExplainerBody>
          </ExplainerText>
        </Explainer>
      </Wrap>
    )
  }

  if (loading) {
    return (
      <Wrap>
        <SkeletonLine $w='260px' $h={18} />
        <RoleCards>
          {[0, 1, 2].map(i => (
            <RoleCard key={i}>
              <SkeletonLine $w='160px' $h={14} />
              <SkeletonLine $w='120px' $h={12} />
            </RoleCard>
          ))}
        </RoleCards>
        <SkeletonLine $w='100%' $h={44} />
        <SkeletonLine $w='100%' $h={44} />
      </Wrap>
    )
  }

  if (!buildings.length) {
    return <Empty>No accessible Building Passports in this {scopeLabel} yet.</Empty>
  }

  const bulkMeta = bulkRole ? roleMeta.find(r => r.key === bulkRole) : null
  const bulkIds = bulkRole ? missingIdsFor(bulkRole) : []

  return (
    <Wrap>
      <Head>
        <HeadText>
          <Title>Safety roles across this {scopeLabel}</Title>
          <Sub>
            See who holds each role on every building. Set a role for all the buildings still to do
            at once, or click any cell in the table to change that one building.
          </Sub>
        </HeadText>
        <SafetyRolesExplainer align='left' />
      </Head>

      <RoleCards>
        {roleMeta.map(role => {
          const totals = totalsFor(role.key)
          return (
            <RoleCard key={role.key}>
              <RoleCardName>{role.label}</RoleCardName>
              {ROLE_PURPOSE[role.key] && (
                <RoleCardPurpose>{ROLE_PURPOSE[role.key]}</RoleCardPurpose>
              )}
              {totals.missing === 0 ? (
                <AllCovered>
                  Every building is covered. {totals.assigned} with a dutyholder
                  {totals.na > 0 && `, ${totals.na} where this role does not apply`}.
                </AllCovered>
              ) : (
                <NeedsAttention>
                  <strong>{totals.missing}</strong> of {totals.total} building{totals.total === 1 ? '' : 's'}
                  {' '}still {totals.missing === 1 ? 'needs' : 'need'} a dutyholder
                </NeedsAttention>
              )}
              <TotalsRow>
                <Total $tint={RAG.low.fg}>{totals.assigned} covered</Total>
                <Total $tint={ink.mute}>{totals.na} not needed here</Total>
                <Total $tint={RAG.medium.fg}>{totals.missing} still to do</Total>
              </TotalsRow>
              {totals.missing > 0 && (
                <BulkActions>
                  <PrimaryBulkBtn
                    type='button'
                    aria-expanded={bulkRole === role.key}
                    onClick={() => { setBulkRole(bulkRole === role.key ? null : role.key); setCellEdit(null) }}
                  >
                    Set for {totals.missing === 1 ? 'the 1 building' : `all ${totals.missing} buildings`} still to do
                  </PrimaryBulkBtn>
                </BulkActions>
              )}
            </RoleCard>
          )
        })}
      </RoleCards>

      {bulkMeta && bulkIds.length > 0 && (
        <SafetyRoleEditor
          key={`bulk-${bulkMeta.key}`}
          scope={scope}
          roleKey={bulkMeta.key}
          roleLabel={bulkMeta.label}
          targetIds={bulkIds}
          targetLabel={bulkIds.length === 1 ? 'the 1 building still to do' : `the ${bulkIds.length} buildings still to do`}
          bulk
          candidates={candidates}
          organisations={organisations}
          onApplied={() => { setBulkRole(null); afterChange() }}
          onCancel={() => setBulkRole(null)}
          idPrefix={`bulk-${bulkMeta.key}`}
        />
      )}

      <TableHead>
        <SearchWrap>
          <SearchRoundedIcon style={{ fontSize: 16, color: ink.mute }} />
          <SearchInput
            placeholder='Search buildings…'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchWrap>
        <Count>Showing {shown.length} of {filtered.length}</Count>
      </TableHead>

      <Matrix>
        <MatrixHeadRow>
          <MatrixBuildingHead>Building Passport</MatrixBuildingHead>
          {roleMeta.map(role => (
            <MatrixRoleHead key={role.key} title={role.label}>{shortLabel(role.label)}</MatrixRoleHead>
          ))}
          <MatrixOpenHead />
        </MatrixHeadRow>

        {shown.map(bp => {
          const editing = cellEdit && cellEdit.bpId === bp.id
            ? (bp.roles || []).find(x => x.key === cellEdit.roleKey)
            : null
          const editingMeta = editing ? roleMeta.find(r => r.key === editing.key) : null
          return (
            <React.Fragment key={bp.id}>
              <MatrixRow>
                <MatrixBuilding type='button' title={`Open ${bp.name}`} onClick={() => navigate(`/bp-list/${bp.id}`)}>{bp.name}</MatrixBuilding>
                {roleMeta.map(role => {
                  const r = (bp.roles || []).find(x => x.key === role.key)
                  const state = r?.state || 'missing'
                  const open = cellEdit && cellEdit.bpId === bp.id && cellEdit.roleKey === role.key
                  const holderNames = (r?.assignees || []).map(a => a.name).filter(Boolean).join(', ')
                  return (
                    <MatrixCell key={role.key}>
                      {bp.canManage === false ? renderStateChip(state) : (
                        <ChipButton
                          type='button'
                          aria-expanded={!!open}
                          $open={!!open}
                          title={holderNames ? `${role.label}: ${holderNames}. Click to change.` : `Set the ${role.label} for ${bp.name}`}
                          aria-label={`${role.label} for ${bp.name}: ${state === 'assigned' ? (holderNames || 'set') : state === 'na' ? 'not applicable' : 'not set'}. ${CELL_ACTION[state] || 'Set'} it`}
                          onClick={() => { openCell(bp, role); setBulkRole(null) }}
                        >
                          {renderStateChip(state)}
                          <ChipAction>{CELL_ACTION[state] || 'Set'}</ChipAction>
                        </ChipButton>
                      )}
                    </MatrixCell>
                  )
                })}
                <MatrixOpen>
                  <OpenLink type='button' title={`Open ${bp.name} at its Access tab, where people and roles are managed`} onClick={() => navigate(`/bp-list/${bp.id}/accessControl`)}>Open</OpenLink>
                </MatrixOpen>
              </MatrixRow>
              {editing && editingMeta && (
                <CellEditorWrap>
                  <SafetyRoleEditor
                    key={`${bp.id}-${editing.key}`}
                    scope={scope}
                    roleKey={editing.key}
                    roleLabel={editingMeta.label}
                    targetIds={[bp.id]}
                    targetLabel={bp.name}
                    bpId={bp.id}
                    state={editing.state}
                    holders={editing.assignees || []}
                    candidates={candidates}
                    organisations={organisations}
                    onApplied={afterChange}
                    onCancel={() => setCellEdit(null)}
                    idPrefix={`cell-${bp.id}-${editing.key}`}
                  />
                </CellEditorWrap>
              )}
            </React.Fragment>
          )
        })}
      </Matrix>

      <InfiniteScrollSentinel
        hasMore={visible < filtered.length}
        loading={false}
        onLoadMore={() => setVisible(v => v + PAGE)}
      />
    </Wrap>
  )
}

export default RoleCoverageView

const shortLabel = label => {
  if (/Responsible/i.test(label)) return 'RP'
  if (/Accountable/i.test(label)) return 'PAP'
  if (/Designer/i.test(label)) return 'PD'
  return label
}

const renderStateChip = state => {
  if (state === 'assigned') {
    return <Chip $tint={RAG.low.fg}><CheckCircleRoundedIcon style={{ fontSize: 13 }} /> Set</Chip>
  }
  if (state === 'na') {
    return <Chip $tint={ink.mute}><RemoveCircleOutlineRoundedIcon style={{ fontSize: 13 }} /> N/A</Chip>
  }
  return <Chip $tint={RAG.medium.fg}><ErrorOutlineRoundedIcon style={{ fontSize: 13 }} /> Not set</Chip>
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Explainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};

  svg { flex-shrink: 0; margin-top: 1px; color: ${({ theme }) => theme.colors.secondary}; }
`

const ExplainerText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ExplainerTitle = styled.span`
  font-size: ${font.small};
  font-weight: 700;
  color: ${ink.base};
`

const ExplainerBody = styled.span`
  font-size: ${font.caption};
  line-height: 1.5;
  color: ${ink.mute};
`

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const HeadText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Sub = styled.span`
  font-size: ${font.caption};
  line-height: 1.45;
  color: ${ink.mute};
`

const RoleCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`

const RoleCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const RoleCardName = styled.span`
  font-size: ${font.small};
  font-weight: 700;
  color: ${ink.base};
`

const RoleCardPurpose = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${font.caption};
  line-height: 1.45;
  color: ${ink.mute};
`

const NeedsAttention = styled.div`
  margin-top: 8px;
  font-size: ${font.caption};
  line-height: 1.45;
  color: ${ink.base};

  strong { font-weight: 700; }
`

const AllCovered = styled.div`
  margin-top: 8px;
  font-size: ${font.caption};
  line-height: 1.45;
  color: ${RAG.low.fg};
`

const TotalsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Total = styled.span`
  padding: 2px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  color: ${({ $tint }) => $tint};
  font-size: ${font.caption};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const BulkActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const PrimaryBulkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 6px 12px;
  border: 1px solid ${RAG.low.fg};
  border-radius: ${radius.pill};
  background: ${RAG.low.fg};
  color: #fff;
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: default; }
  &:hover:not(:disabled) { filter: brightness(0.95); }
`

const TableHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const SearchWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.pill};
  background: ${surface.card};
  min-width: 220px;
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: ${font.small};
  color: ${ink.base};
  width: 100%;
`

const Count = styled.span`
  font-size: 14px;
  color: #475569;
  font-variant-numeric: tabular-nums;
`

const Matrix = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  overflow: hidden;
`

const MatrixHeadRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) repeat(3, minmax(70px, 1fr)) 72px;
  gap: 8px;
  padding: 9px 14px;
  background: ${surface.wash};
  border-bottom: 1px solid ${line.base};

  @media (max-width: ${bpToken.mobile}) {
    grid-template-columns: repeat(3, minmax(0, 1fr)) 56px;
    gap: 6px;
    padding: 9px 10px;
  }
`

const MatrixBuildingHead = styled.span`
  @media (max-width: ${bpToken.mobile}) { display: none; }
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const MatrixRoleHead = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  color: ${ink.mute};
  text-align: center;
`

const MatrixOpenHead = styled.span``

const MatrixRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) repeat(3, minmax(70px, 1fr)) 72px;
  gap: 8px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid ${line.base};

  &:last-child { border-bottom: none; }

  @media (max-width: ${bpToken.mobile}) {
    grid-template-columns: repeat(3, minmax(0, 1fr)) 56px;
    gap: 6px;
    padding: 10px;
  }
`

const MatrixBuilding = styled.button`
  @media (max-width: ${bpToken.mobile}) {
    grid-column: 1 / -1;
    font-size: 15px;
  }
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${font.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const MatrixCell = styled.span`
  display: flex;
  justify-content: center;
  min-width: 0;
`

const MatrixOpen = styled.span`
  display: flex;
  justify-content: flex-end;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  color: ${({ $tint }) => $tint};
  font-size: ${font.caption};
  font-weight: 700;
  white-space: nowrap;
`

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  @media (max-width: ${bpToken.mobile}) {
    flex-direction: column;
    gap: 2px;
    padding: 4px 6px;
  }

  min-height: 40px;
  padding: 0 6px;
  border: 1px solid ${({ $open, theme }) => ($open ? theme.colors.secondary : 'transparent')};
  border-radius: ${radius.pill};
  background: ${({ $open }) => ($open ? '#e6f7f9' : 'transparent')};
  font: inherit;
  cursor: pointer;

  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; background: #e6f7f9; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.secondary}; outline-offset: 2px; }
`

const ChipAction = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #17727b;
  text-decoration: underline;
  text-underline-offset: 2px;
`

const CellEditorWrap = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${line.base};
  background: ${surface.wash};
`

const OpenLink = styled.button`
  padding: 4px 10px;
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;

  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.secondary}; }
`

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`
